import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Boxes, ClipboardCheck, Package, Plus, Trash2, RefreshCw, Truck, Building2 } from "lucide-react";

/**
 * Warehouse dashboard — one page, three tabs.
 *
 * Access: admin + warehouse_staff (backend enforces).
 *
 * Tabs:
 *   Queue      - warehouse_orders in Paid → Assigned → Picking → Packed → Labeled → Shipped
 *   Inventory  - SKUs with on-hand / reserved / reorder
 *   Master Boxes - templates (SKU + qty) that auto-populate a drop's packing checklist
 */

const STATES = ["paid", "assigned", "picking", "packed", "labeled", "shipped"];
const STATE_LABELS = { paid: "Paid", assigned: "Assigned", picking: "Picking",
                       packed: "Packed", labeled: "Labeled", shipped: "Shipped" };

export default function Warehouse() {
  const [tab, setTab] = useState("queue");
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(() => localStorage.getItem("wh_selected") || "all");

  useEffect(() => {
    api.get("/warehouse/warehouses").then(r => setWarehouses(r.data || [])).catch(() => {});
  }, []);

  const setWh = (id) => {
    setWarehouseId(id);
    localStorage.setItem("wh_selected", id);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="warehouse-page">
      <div className="flex items-center gap-3 flex-wrap mb-6">
        <h1 className="font-display text-4xl">Warehouse</h1>
        <div className="ml-auto flex items-center gap-2">
          {[
            {id:"queue", l:"Packing queue", icon: ClipboardCheck},
            {id:"inventory", l:"Inventory", icon: Boxes},
            {id:"boxes", l:"Master boxes", icon: Package},
          ].map(t=>(
            <button
              key={t.id}
              onClick={()=>setTab(t.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm transition-colors border ${
                tab===t.id ? "bg-[var(--ff-primary)] text-white border-transparent"
                           : "border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"
              }`}
              data-testid={`wh-tab-${t.id}`}
            >
              <t.icon size={14}/> {t.l}
            </button>
          ))}
        </div>
      </div>

      <WarehouseSelector
        warehouses={warehouses}
        selected={warehouseId}
        onChange={setWh}
      />

      {tab === "queue" && <QueueTab warehouseId={warehouseId}/>}
      {tab === "inventory" && <InventoryTab warehouseId={warehouseId} warehouses={warehouses}/>}
      {tab === "boxes" && <MasterBoxesTab/>}
    </div>
  );
}

function WarehouseSelector({ warehouses, selected, onChange }) {
  return (
    <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="wh-selector">
      <span className="text-xs text-[var(--ff-text-muted)] inline-flex items-center gap-1">
        <Building2 size={12}/> Warehouse:
      </span>
      <button
        onClick={() => onChange("all")}
        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
          selected === "all"
            ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]"
            : "bg-white border-stone-300 text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"
        }`}
        data-testid="wh-selector-all"
      >
        All warehouses
      </button>
      {warehouses.map(w => (
        <button
          key={w.id}
          onClick={() => onChange(w.id)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            selected === w.id
              ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]"
              : "bg-white border-stone-300 text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"
          }`}
          data-testid={`wh-selector-${w.id}`}
          title={`${w.origin_address?.city}, ${w.origin_address?.state} · ${w.origin_address?.zip}`}
        >
          {w.name.replace(" Fulfillment Center", "")} <span className="opacity-60 ml-1">{w.origin_address?.state}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

function QueueTab({ warehouseId = "all" }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("paid");
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const params = { state: filter, limit: 200 };
    if (warehouseId && warehouseId !== "all") params.warehouse_id = warehouseId;
    const { data } = await api.get("/warehouse/orders", { params });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, warehouseId]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/warehouse/sync-paid");
      alert(`Pulled ${data.synced} paid orders into the queue (${data.total_paid} total paid).`);
      await load();
    } finally { setSyncing(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {STATES.map(s => (
          <button
            key={s}
            onClick={()=>setFilter(s)}
            className={`rounded-full px-3 py-1 text-xs uppercase tracking-wider transition-colors ${
              filter === s ? "bg-[var(--ff-primary)] text-white" : "bg-[var(--ff-muted)] text-[var(--ff-text-secondary)]"
            }`}
            data-testid={`wh-filter-${s}`}
          >
            {STATE_LABELS[s]}
          </button>
        ))}
        <div className="ml-auto">
          <button onClick={sync} disabled={syncing}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ff-primary)] hover:underline disabled:opacity-50"
                  data-testid="wh-sync-paid">
            <RefreshCw size={12} className={syncing ? "animate-spin" : ""}/> Sync paid reservations
          </button>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        {rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-[var(--ff-text-muted)]" data-testid="wh-queue-empty">
            No orders in <strong>{STATE_LABELS[filter]}</strong>.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--ff-muted)]">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Order</th>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Warehouse</th>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Drop</th>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Buyer</th>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Qty</th>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Assignee</th>
                <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider">Tracking</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[var(--ff-border)]" data-testid={`wh-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.id.slice(0,8)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-2 py-0.5" data-testid={`wh-chip-${r.id}`}>
                      {(r.warehouse_id || "wh_fl").replace("wh_", "").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">{r.drop_slug}</td>
                  <td className="px-4 py-3 text-xs">{r.buyer_email || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{r.quantity}</td>
                  <td className="px-4 py-3 text-xs">{r.assignee || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.tracking_number || "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={()=>setSelected(r)} className="text-xs text-[var(--ff-primary)] font-semibold hover:underline">Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <OrderDrawer wh={selected} onClose={()=>{setSelected(null); load();}}/>}
    </div>
  );
}

function OrderDrawer({ wh, onClose }) {
  const [order, setOrder] = useState(wh);
  const load = async () => {
    const { data } = await api.get(`/warehouse/orders/${wh.id}`);
    setOrder(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wh.id]);

  const togglePacked = async (line) => {
    await api.patch(`/warehouse/orders/${wh.id}/checklist`, {
      sku_id: line.sku_id,
      packed: !line.packed,
    });
    await load();
  };

  const transition = async (to_state, extras = {}) => {
    try {
      await api.post(`/warehouse/orders/${wh.id}/transition`, { to_state, ...extras });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Transition failed");
    }
  };

  const currentIdx = STATES.indexOf(order.state || "paid");
  const nextState = STATES[currentIdx + 1];

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg overflow-y-auto p-6 shadow-2xl"
        onClick={e=>e.stopPropagation()}
        data-testid="wh-order-drawer"
      >
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <h2 className="font-display text-2xl">Order {order.id.slice(0,8)}</h2>
          <button onClick={onClose} className="text-sm text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]">Close</button>
        </div>
        <p className="text-sm text-[var(--ff-text-secondary)]">
          <strong>{order.drop_slug}</strong> · qty {order.quantity} · buyer {order.buyer_email || "unknown"}
        </p>

        {/* State stepper */}
        <div className="mt-5 flex items-center flex-wrap gap-1.5">
          {STATES.map((s, i) => (
            <span
              key={s}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                i <= currentIdx ? "bg-[var(--ff-primary)] text-white" : "bg-[var(--ff-muted)] text-[var(--ff-text-muted)]"
              }`}
            >
              {STATE_LABELS[s]}
            </span>
          ))}
        </div>

        {/* Checklist */}
        <h3 className="font-semibold mt-6 mb-2">Packing checklist</h3>
        {(!order.checklist || order.checklist.length === 0) ? (
          <p className="text-sm text-[var(--ff-text-muted)]">No checklist — link a Master Box to this drop first.</p>
        ) : (
          <ul className="divide-y divide-[var(--ff-border)]">
            {order.checklist.map(line => (
              <li key={line.sku_id} className="py-2.5 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={!!line.packed}
                  onChange={()=>togglePacked(line)}
                  className="h-5 w-5 accent-[var(--ff-primary)]"
                  data-testid={`wh-checkbox-${line.sku_id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{line.sku_name || line.sku_code}</p>
                  <p className="text-xs text-[var(--ff-text-muted)]">
                    Need <strong>{line.required_qty} {line.unit}</strong>
                    {line.packed && line.packed_qty ? ` · packed ${line.packed_qty}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Transition */}
        <div className="mt-6 space-y-3">
          {nextState === "labeled" && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <input placeholder="Tracking #" className="input-base col-span-2" id="wh-track"/>
              <input placeholder="Weight (lb)" type="number" step="0.1" className="input-base" id="wh-w"/>
              <input placeholder="L in" type="number" step="0.1" className="input-base" id="wh-l"/>
              <input placeholder="W in" type="number" step="0.1" className="input-base" id="wh-wd"/>
              <input placeholder="H in" type="number" step="0.1" className="input-base" id="wh-h"/>
            </div>
          )}
          {nextState ? (
            <button
              onClick={()=>{
                const extras = {};
                if (nextState === "labeled") {
                  const t=document.getElementById("wh-track")?.value;
                  if (t) extras.tracking_number = t;
                  const w=document.getElementById("wh-w")?.value;
                  if (w) extras.package_weight_lb = parseFloat(w);
                  const l=document.getElementById("wh-l")?.value;
                  if (l) extras.package_length_in = parseFloat(l);
                  const wd=document.getElementById("wh-wd")?.value;
                  if (wd) extras.package_width_in = parseFloat(wd);
                  const h=document.getElementById("wh-h")?.value;
                  if (h) extras.package_height_in = parseFloat(h);
                }
                transition(nextState, extras);
              }}
              className="btn-primary w-full justify-center inline-flex items-center gap-2"
              data-testid={`wh-advance-${nextState}`}
            >
              <Truck size={14}/> Advance to {STATE_LABELS[nextState]}
            </button>
          ) : (
            <p className="text-center text-sm text-[var(--ff-text-muted)]">Order shipped — no further transitions.</p>
          )}
        </div>

        {/* History */}
        <details className="mt-6 text-xs">
          <summary className="cursor-pointer text-[var(--ff-text-secondary)]">State history ({(order.state_history||[]).length})</summary>
          <ul className="mt-2 space-y-1">
            {(order.state_history||[]).map((h,i)=>(
              <li key={i} className="text-[var(--ff-text-muted)]">
                {h.at?.slice(0,16).replace("T"," ")} · <strong>{STATE_LABELS[h.state]}</strong> by {h.by}
                {h.note ? ` — ${h.note}` : ""}
              </li>
            ))}
          </ul>
        </details>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

function InventoryTab({ warehouseId = "all", warehouses = [] }) {
  const [items, setItems] = useState([]);
  const defaultWh = warehouseId !== "all" ? warehouseId : (warehouses[0]?.id || "wh_fl");
  const [form, setForm] = useState({ sku: "", name: "", unit: "each", reorder_point: 5, warehouse_id: defaultWh });
  const load = async () => {
    const params = {};
    if (warehouseId && warehouseId !== "all") params.warehouse_id = warehouseId;
    const { data } = await api.get("/warehouse/inventory", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [warehouseId]);
  useEffect(() => {
    // Keep the "Add SKU" form pointed at the currently-selected warehouse.
    setForm(f => ({ ...f, warehouse_id: warehouseId !== "all" ? warehouseId : (warehouses[0]?.id || "wh_fl") }));
  }, [warehouseId, warehouses]);

  const create = async () => {
    try {
      await api.post("/warehouse/inventory", form);
      setForm({ sku: "", name: "", unit: "each", reorder_point: 5 });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Create failed"); }
  };
  const adjust = async (id) => {
    const raw = prompt("Adjustment delta (positive = restock, negative = deduction):");
    if (raw === null) return;
    const reason = prompt("Reason:") || "manual adjust";
    await api.post(`/warehouse/inventory/${id}/adjust`, { delta: parseInt(raw, 10), reason });
    await load();
  };
  const del = async (id) => {
    if (!confirm("Delete this SKU?")) return;
    await api.delete(`/warehouse/inventory/${id}`);
    await load();
  };

  return (
    <div>
      <div className="card-flat p-4 mb-6" data-testid="wh-inv-create">
        <p className="overline">Add SKU</p>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-2">
          <input placeholder="SKU code" className="input-base text-sm" value={form.sku} onChange={e=>setForm({...form, sku:e.target.value})}/>
          <input placeholder="Name" className="input-base text-sm sm:col-span-2" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
          <select className="input-base text-sm" value={form.unit} onChange={e=>setForm({...form, unit:e.target.value})}>
            <option value="each">each</option>
            <option value="lb">lb</option>
            <option value="oz">oz</option>
            <option value="pack">pack</option>
          </select>
          <select
            className="input-base text-sm"
            value={form.warehouse_id}
            onChange={e=>setForm({...form, warehouse_id: e.target.value})}
            data-testid="wh-inv-create-warehouse"
          >
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name.replace(" Fulfillment Center", "")}</option>
            ))}
          </select>
          <button onClick={create} className="btn-primary inline-flex items-center justify-center gap-1 text-sm" data-testid="wh-inv-create-btn">
            <Plus size={14}/> Add
          </button>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--ff-muted)]">
            <tr><th className="text-left px-4 py-2 text-xs uppercase tracking-wider">SKU</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Warehouse</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Unit</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">On hand</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Reserved</th>
                <th></th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-[var(--ff-border)]" data-testid={`wh-inv-${it.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{it.sku}</td>
                <td className="px-4 py-3">{it.name}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-2 py-0.5">
                    {(it.warehouse_id || "wh_fl").replace("wh_", "").toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{it.unit}</td>
                <td className="px-4 py-3 tabular-nums font-semibold">
                  <span className={((it.on_hand||0) <= (it.reorder_point||0)) ? "text-rose-700" : ""}>{it.on_hand || 0}</span>
                </td>
                <td className="px-4 py-3 tabular-nums text-[var(--ff-text-muted)]">{it.reserved || 0}</td>
                <td className="px-4 py-3 text-xs space-x-3">
                  <button onClick={()=>adjust(it.id)} className="text-[var(--ff-primary)] font-semibold hover:underline">Adjust</button>
                  <button onClick={()=>del(it.id)} className="text-rose-700 hover:underline"><Trash2 size={12} className="inline"/></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-[var(--ff-text-muted)]">No SKUs yet — add one above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Master Boxes
// ---------------------------------------------------------------------------

function MasterBoxesTab() {
  const [boxes, setBoxes] = useState([]);
  const [skus, setSkus] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", lines: [] });
  const load = async () => {
    const [b, s] = await Promise.all([api.get("/warehouse/master-boxes"), api.get("/warehouse/inventory")]);
    setBoxes(b.data);
    setSkus(s.data);
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { sku_id: skus[0]?.id || "", qty: 1 }] }));
  const setLine = (i, patch) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], ...patch };
    return { ...f, lines };
  });
  const rmLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_,idx)=>idx!==i) }));

  const create = async () => {
    if (!form.name || form.lines.length === 0) return alert("Name + at least one SKU required.");
    try {
      await api.post("/warehouse/master-boxes", form);
      setForm({ name: "", description: "", lines: [] });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Create failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this master box template?")) return;
    await api.delete(`/warehouse/master-boxes/${id}`);
    await load();
  };

  return (
    <div>
      <div className="card-flat p-5 mb-6" data-testid="wh-box-create">
        <p className="overline">Create master box template</p>
        <input placeholder="Box name (e.g. Weekly Wagyu Sampler)" className="input-base text-sm w-full mt-2" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
        <input placeholder="Description (optional)" className="input-base text-sm w-full mt-2" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/>
        <div className="mt-3 space-y-2">
          {form.lines.map((ln, i) => (
            <div key={i} className="flex items-center gap-2">
              <select className="input-base text-sm flex-1" value={ln.sku_id} onChange={e=>setLine(i,{sku_id:e.target.value})}>
                {skus.map(s => <option key={s.id} value={s.id}>{s.sku} — {s.name}</option>)}
              </select>
              <input type="number" min={1} className="input-base text-sm w-20" value={ln.qty} onChange={e=>setLine(i,{qty:parseInt(e.target.value)||1})}/>
              <button onClick={()=>rmLine(i)} className="text-rose-700"><Trash2 size={14}/></button>
            </div>
          ))}
          <button onClick={addLine} className="text-xs text-[var(--ff-primary)] font-semibold hover:underline"
                  disabled={skus.length===0}>
            + Add SKU line{skus.length===0 ? " (add SKUs in Inventory tab first)" : ""}
          </button>
        </div>
        <button onClick={create} className="btn-primary mt-4 inline-flex items-center gap-1 text-sm" data-testid="wh-box-create-btn">
          <Plus size={14}/> Create template
        </button>
      </div>

      <div className="space-y-3">
        {boxes.map(b => (
          <div key={b.id} className="card-flat p-4" data-testid={`wh-box-${b.id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <h3 className="font-semibold">{b.name}</h3>
                <p className="text-xs text-[var(--ff-text-muted)]">{b.description || "—"} · id <code>{b.id.slice(0,8)}</code></p>
              </div>
              <button onClick={()=>del(b.id)} className="text-xs text-rose-700 hover:underline">Delete</button>
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {b.lines?.map(ln => (
                <li key={ln.sku_id} className="text-[var(--ff-text-secondary)]">
                  {ln.qty}× <strong>{ln.sku_name || ln.sku_id}</strong> {ln.sku_code ? <code className="text-xs">({ln.sku_code})</code> : null}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[var(--ff-text-muted)] mt-3">
              Link this box to a drop: set the drop&apos;s <code>master_box_id</code> to <code>{b.id}</code> in the admin drop editor.
            </p>
          </div>
        ))}
        {boxes.length === 0 && <p className="text-center text-sm text-[var(--ff-text-muted)]">No master boxes yet.</p>}
      </div>
    </div>
  );
}
